/**
 * Moving a drawing's points. §6.4.
 *
 * The same gestures as every vector tool, because anyone who has edited a path
 * before will reach for them without reading anything:
 *
 *   drag a point           moves it, handles and all
 *   drag a handle          bends the curve; on a smooth point the other handle
 *                          turns with it, so the curve stays smooth
 *   Alt-drag a handle      moves that handle alone
 *   Alt-click a point      corner to curve, or curve to corner
 *   double-click outline   adds a point there, without changing the shape
 *   Delete                 removes the chosen point
 *   Tab, arrows            choose a point, and move it, without a mouse
 *   Enter, Escape          finished
 *
 * Every change goes into the document as it happens, and a drag is one undo
 * step. That is unlike the pen, which keeps a half-drawn path to itself: a
 * drawing being edited already belongs to the customer, and undo should work on
 * it the way it works on everything else they own.
 *
 * The overlay is drawn in the layer's own frame - turned and flipped with it -
 * so points sit on the drawing whatever has been done to it. The pointer is
 * not read through that frame, though. It is read on the page and converted
 * with the same arithmetic the press uses (shapes/nodes.ts), because the frame
 * moves under the pointer every time a point pushes the box wider, and a
 * position read through a frame that is moving is a position read wrong.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, Group, Line, Path, Rect } from 'react-konva';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { mmToPx, pxToMm } from '../model/units';
import type { PathNode, ShapeLayer } from '../model/types';
import { findLayer } from '../model/types';
import { freePath } from '../shapes/library';
import {
  isSmooth,
  local,
  localToPage,
  moveAnchor,
  moveHandle,
  nearest,
  pageToLocal,
  remove,
  split,
  toggleSmooth,
  type Point,
} from '../shapes/nodes';
import { setPath } from '../state/actions';
import { frameOf } from './frame';
import { isTyping } from './CanvasView';

const ACCENT = '#dd1d26';

/** A double-click this close to the outline adds a point; further off it is a miss. */
const ADD_WITHIN = 10;

/** Arrow keys, as for nudging a whole object. */
const NUDGE = 0.5;
const NUDGE_BIG = 5;

export function NodeEditor( { zoom }: { zoom: number } ) {
  const shaping = useEditor( ( s ) => s.shaping );
  const preview = useEditor( ( s ) => s.view.preview );
  const page = useEditor( ( s ) => s.doc?.pages[ s.pageIndex ] ?? null );

  if ( preview || ! shaping || ! page ) return null;

  const layer = findLayer( page.layers, shaping );

  if ( ! layer || layer.type !== 'shape' || layer.shape !== 'free' || layer.locked ) return null;

  return <Points key={ layer.id } layer={ layer } zoom={ zoom } />;
}

/** Which thing is under the button. */
type Grip = { kind: 'anchor' } | { kind: 'handle'; which: 'in' | 'out' };

function Points( { layer, zoom }: { layer: ShapeLayer; zoom: number } ) {
  const group = useRef<Konva.Group>( null );

  /** The chosen point: the one Delete, Tab and the arrows act on. */
  const [ chosen, setChosen ] = useState<number | null>( null );

  // The key handler is registered once and reads these, rather than being
  // torn down and put back on every change of the drawing.
  const live = useRef( { layer, chosen } );

  live.current = { layer, chosen };

  const nodes = local( layer );
  const w = mmToPx( layer.w, zoom );
  const h = mmToPx( layer.h, zoom );
  const px = ( p: Point ) => ( { x: mmToPx( p.x, zoom ), y: mmToPx( p.y, zoom ) } );

  /** The drawing as it is in the store now, which during a drag is newer than this render. */
  const current = useCallback( (): ShapeLayer | null => {
    const store = useEditor.getState();
    const page = store.doc?.pages[ store.pageIndex ];
    const found = page ? findLayer( page.layers, layer.id ) : null;

    return found && found.type === 'shape' ? found : null;
  }, [ layer.id ] );

  /** Where the pointer is on the page, in millimetres. */
  const pointer = useCallback( ( event: MouseEvent ): Point | null => {
    const stage = group.current?.getStage();

    if ( ! stage ) return null;

    const view = useEditor.getState().view;
    const rect = stage.container().getBoundingClientRect();

    return {
      x: pxToMm( event.clientX - rect.left - view.x, view.zoom ),
      y: pxToMm( event.clientY - rect.top - view.y, view.zoom ),
    };
  }, [] );

  /* --- dragging --------------------------------------------- */

  const grab = useCallback(
    ( index: number, grip: Grip ) => ( event: Konva.KonvaEventObject<MouseEvent> ) => {
      if ( event.evt.button !== 0 ) return;

      const start = current();
      const at = pointer( event.evt );

      if ( ! start || ! at ) return;

      setChosen( index );

      // Alt on a point is a click, not a drag: it changes what kind of point
      // this is and leaves it where it is.
      if ( grip.kind === 'anchor' && event.evt.altKey ) {
        setPath( layer.id, toggleSmooth( local( start ), !! start.closed, index ), 'Change point' );

        return;
      }

      const node = local( start )[ index ];
      const target = grip.kind === 'anchor' ? node : node?.[ grip.which ];

      if ( ! node || ! target ) return;

      /*
       * How far the pointer is from the thing it picked up. Kept, so that the
       * thing does not jump to sit exactly under the pointer on the first move -
       * a few pixels, but on a small drawing a few pixels is the whole point.
       * A vector in the layer's own axes, which a refitted box does not turn.
       */
      const from = pageToLocal( start, at );
      const offset = { x: target.x - from.x, y: target.y - from.y };
      const key = `points:${ layer.id }:${ Date.now() }`;

      const move = ( moved: MouseEvent ) => {
        const now = current();
        const point = pointer( moved );

        if ( ! now || ! point ) return;

        const p = pageToLocal( now, point );
        const to = { x: p.x + offset.x, y: p.y + offset.y };
        const before = local( now );

        const next =
          grip.kind === 'anchor'
            ? moveAnchor( before, index, to )
            : moveHandle( before, index, grip.which, to, moved.altKey );

        setPath( layer.id, next, 'Edit points', key );
      };

      const up = () => {
        window.removeEventListener( 'mousemove', move );
        window.removeEventListener( 'mouseup', up );
      };

      window.addEventListener( 'mousemove', move );
      window.addEventListener( 'mouseup', up );
    },
    [ current, pointer, layer.id ]
  );

  /* --- adding ----------------------------------------------- */

  const onDblClick = useCallback(
    ( event: Konva.KonvaEventObject<MouseEvent> ) => {
      const now = current();
      const at = pointer( event.evt );

      if ( ! now || ! at ) return;

      const points = local( now );
      const hit = nearest( points, !! now.closed, pageToLocal( now, at ) );

      if ( ! hit || hit.distance > pxToMm( ADD_WITHIN, zoom ) ) return;

      const made = split( points, !! now.closed, hit.segment, hit.t );

      if ( made.index < 0 ) return;

      setPath( layer.id, made.nodes, 'Add a point' );
      setChosen( made.index );
    },
    [ current, pointer, layer.id, zoom ]
  );

  /* --- the keyboard ----------------------------------------- */

  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( isTyping( event.target ) ) return;

      const { layer: now, chosen: index } = live.current;
      const name = event.key;
      const store = useEditor.getState();

      // Taken before the editor's own shortcuts, which would otherwise delete
      // the drawing, nudge the whole of it, or move focus off the canvas.
      const stop = () => {
        event.preventDefault();
        event.stopImmediatePropagation();
      };

      if ( name === 'Escape' || name === 'Enter' ) {
        stop();
        store.endShaping();

        return;
      }

      const points = local( now );

      if ( name === 'Tab' ) {
        stop();

        const n = points.length;
        const from = index ?? ( event.shiftKey ? 0 : -1 );

        setChosen( ( ( from + ( event.shiftKey ? -1 : 1 ) ) % n + n ) % n );

        return;
      }

      if ( name === 'Delete' || name === 'Backspace' ) {
        // Always taken: with no point chosen, Delete doing nothing is better
        // than Delete removing the drawing the customer is in the middle of.
        stop();

        if ( index === null ) return;

        const next = remove( points, index );

        if ( next === points ) return;

        setPath( now.id, next, 'Remove a point' );
        setChosen( null );

        return;
      }

      if ( name.startsWith( 'Arrow' ) && index !== null && points[ index ] ) {
        stop();

        const step = event.shiftKey ? NUDGE_BIG : NUDGE;
        const dx = name === 'ArrowLeft' ? -step : name === 'ArrowRight' ? step : 0;
        const dy = name === 'ArrowUp' ? -step : name === 'ArrowDown' ? step : 0;

        // Along the page's axes, not the drawing's: right means right on
        // screen, whichever way the drawing has been turned.
        const onPage = localToPage( now, points[ index ] as PathNode );
        const to = pageToLocal( now, { x: onPage.x + dx, y: onPage.y + dy } );

        setPath( now.id, moveAnchor( points, index, to ), 'Move a point', `nudge:${ now.id }` );
      }
    };

    window.addEventListener( 'keydown', key, true );

    return () => window.removeEventListener( 'keydown', key, true );
  }, [] );

  /* --- drawing it ------------------------------------------- */

  const data = freePath( layer.nodes ?? [], !! layer.closed, w, h );
  const picked = chosen !== null ? nodes[ chosen ] : undefined;

  const cursor = ( value: string ) => ( event: Konva.KonvaEventObject<MouseEvent> ) => {
    const stage = event.target.getStage();

    if ( stage ) stage.container().style.cursor = value;
  };

  return (
    /*
     * Named for the drawing, like the crop frame, so that clicking on any of
     * this reads as clicking the drawing - and keeps it selected - rather than
     * as clicking bare paper, which would end the editing.
     */
    <Group ref={ group } name={ layer.id } { ...frameOf( layer, zoom ) }>
      <Path data={ data } stroke={ ACCENT } strokeWidth={ 1 } strokeScaleEnabled={ false } listening={ false } />

      {/* A wide, invisible band along the outline, to double-click on. */}
      {/*
        Nothing inside this group carries a name - ids instead, for tests. The
        canvas decides what was clicked from the nearest name going up, so a
        named anchor would read as a layer called "anchor" and deselect the
        drawing.
      */}
      <Path
        id="tb-points-outline"
        data={ data }
        stroke="rgba(0,0,0,0)"
        strokeWidth={ 1 }
        hitStrokeWidth={ ADD_WITHIN * 2 }
        fillEnabled={ false }
        strokeScaleEnabled={ false }
        onDblClick={ onDblClick }
        onMouseEnter={ cursor( 'copy' ) }
        onMouseLeave={ cursor( '' ) }
      />

      {/* The chosen point's handles. Only its own: every handle at once is a thicket. */}
      { picked && chosen !== null &&
        ( [ 'in', 'out' ] as const ).map( ( which ) => {
          const handle = picked[ which ];

          if ( ! handle ) return null;

          const a = px( picked );
          const b = px( handle );

          return (
            <Group key={ which }>
              <Line
                points={ [ a.x, a.y, b.x, b.y ] }
                stroke={ ACCENT }
                strokeWidth={ 1 }
                strokeScaleEnabled={ false }
                listening={ false }
              />
              <Circle
                id={ `tb-handle-${ which }` }
                x={ b.x }
                y={ b.y }
                radius={ 4 }
                fill={ ACCENT }
                hitStrokeWidth={ 8 }
                onMouseDown={ grab( chosen, { kind: 'handle', which } ) }
                onMouseEnter={ cursor( 'pointer' ) }
                onMouseLeave={ cursor( '' ) }
              />
            </Group>
          );
        } ) }

      {/*
        Corners are squares and smooth points are circles - Inkscape's
        convention, and the quickest way to see what an Alt-click will do.
      */}
      { nodes.map( ( node, index ) => {
        const at = px( node );
        const on = index === chosen;
        const common = {
          id: `tb-anchor-${ index }`,
          x: at.x,
          y: at.y,
          fill: on ? ACCENT : '#ffffff',
          stroke: ACCENT,
          strokeWidth: 1.5,
          hitStrokeWidth: 10,
          onMouseDown: grab( index, { kind: 'anchor' } ),
          onMouseEnter: cursor( 'move' ),
          onMouseLeave: cursor( '' ),
        };

        return isSmooth( node ) ? (
          <Circle key={ index } { ...common } radius={ 4.5 } />
        ) : (
          <Rect key={ index } { ...common } width={ 8 } height={ 8 } offsetX={ 4 } offsetY={ 4 } />
        );
      } ) }
    </Group>
  );
}
